import React, { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { c } from "../../classroomColors";
import { brand, onTint } from "../../standeTokens";

/**
 * Who is in the room and what they are doing (wireframe 130:54).
 *
 * Absent is not a separate list to maintain — it is everyone on the roster who
 * has not joined, worked out here so the two tabs can never disagree.
 */
const ACTIVITY_LABEL = {
  answering: "Answering",
  typing: "typing in the chat",
  present: "present",
  idle: "present",
};

const Person = ({ person, isAbsent, isDark }) => {
  const b = brand(isDark);
  const tint = onTint(isDark);
  const active = person.activity === "answering" || person.activity === "typing";

  return (
    <Flex align="flex-start" gap="9px" py="7px">
      <Flex
        flexShrink={0}
        boxSize="24px"
        mt="1px"
        borderRadius="full"
        align="center"
        justify="center"
        bg={isAbsent ? c.surfaceSubtle : active ? b.lightGreen : c.surfaceSubtle}
        color={isAbsent ? c.textMuted : active ? tint.lightGreen : c.textSub}
        fontSize="10.5px"
        fontWeight="600"
        opacity={isAbsent ? 0.7 : 1}
      >
        {(person.name || "?")[0].toUpperCase()}
      </Flex>
      <Box minW={0}>
        <Text fontSize="12.5px" fontWeight="500" color={isAbsent ? c.textMuted : c.text} noOfLines={1}>
          {person.name}
        </Text>
        <Text fontSize="11px" color={c.textMuted} noOfLines={1}>
          {isAbsent ? "not joined" : ACTIVITY_LABEL[person.activity] || "present"}
        </Text>
      </Box>
    </Flex>
  );
};

/**
 * The People panel in the room's dock: two tabs with their counts, and the
 * list under the one chosen.
 */
const AttendancePanel = ({ roster = [], attendingGuids = [], isDark }) => {
  const [tab, setTab] = useState("attending");
  const present = new Set(attendingGuids);
  const attending = roster.filter((p) => present.has(p.guid));
  const absent = roster.filter((p) => !present.has(p.guid));
  const list = tab === "attending" ? attending : absent;

  return (
    <Flex direction="column" minH={0} flex="1 1 auto">
      <Box px="14px" pt="2px" pb="8px">
        <Flex
          gap="3px"
          p="3px"
          borderRadius="10px"
          bg={c.surfaceSubtle}
          border="1px solid"
          borderColor={c.borderSoft}
        >
          {[
            { key: "attending", label: "Attending", n: attending.length },
            { key: "absent", label: "Absent", n: absent.length },
          ].map((t) => (
            <Box
              key={t.key}
              as="button"
              type="button"
              onClick={() => setTab(t.key)}
              flex="1"
              px="11px"
              py="6px"
              borderRadius="8px"
              fontSize="12px"
              fontWeight={tab === t.key ? "500" : "400"}
              bg={tab === t.key ? c.surface : "transparent"}
              color={tab === t.key ? c.text : c.textSub}
              boxShadow={tab === t.key ? "0 1px 2px rgba(0,0,0,0.06)" : "none"}
              transition="background 0.15s, color 0.15s"
            >
              {t.label}
              <Box as="span" ml="5px" color={c.textMuted} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {t.n}
              </Box>
            </Box>
          ))}
        </Flex>
      </Box>

      <Box flex="1 1 auto" minH={0} overflowY="auto" px="14px" pb="10px" data-own-scroll>
        {list.length === 0 ? (
          <Text fontSize="12px" color={c.textMuted} py="18px" textAlign="center">
            {tab === "attending" ? "Nobody has joined yet." : "Everyone is here."}
          </Text>
        ) : (
          list.map((p) => (
            <Person key={p.guid} person={p} isAbsent={tab === "absent"} isDark={isDark} />
          ))
        )}
      </Box>
    </Flex>
  );
};

export default AttendancePanel;
